import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { PAYMENT_PLANS, createCheckoutSession } from '../services/paymentService';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

function PremiumUpgradeModal({ isOpen, onClose, onSuccess }) {
  const [selectedPlan, setSelectedPlan] = useState(PAYMENT_PLANS.MONTHLY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { currentUser } = useAuth();

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  // Handle plan selection
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  // Handle next step in upgrade process
  const handleNextStep = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const userId = currentUser?.uid;
      const userEmail = currentUser?.email;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Log details before making the call for easier debugging
      console.log('Creating checkout session with plan:', selectedPlan, 'user ID:', userId, 'email:', userEmail);
      
      // Make the API call to create a checkout session
      const { url } = await createCheckoutSession(selectedPlan, userId, userEmail);
      
      // Redirect to Stripe checkout
      if (url) {
        console.log('Redirecting to Stripe checkout:', url);
        window.location.href = url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to initialize checkout. Please try again.');
      setIsLoading(false);
    }
  };

  // Render plan selection step
  const renderPlanSelection = () => (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Upgrade to Pro</h2>
      <p className="text-gray-600 mb-6">
        Choose a subscription plan to unlock all Pro features, including unlimited workout saves,
        favorite exercises, and enhanced progress tracking.
      </p>
      
      <div className="space-y-4 mb-8">
        {/* Monthly Plan */}
        <div
          className={`border rounded-lg p-4 transition-all cursor-pointer ${
            selectedPlan.id === PAYMENT_PLANS.MONTHLY.id
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
          }`}
          onClick={() => handleSelectPlan(PAYMENT_PLANS.MONTHLY)}
        >
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-lg">{PAYMENT_PLANS.MONTHLY.name}</h3>
              <p className="text-gray-600 text-sm mt-1">{PAYMENT_PLANS.MONTHLY.description}</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-gray-800">
                ${PAYMENT_PLANS.MONTHLY.price}
              </span>
              <span className="text-gray-500 text-sm">/month</span>
            </div>
          </div>
        </div>
        
        {/* Annual Plan */}
        <div
          className={`border rounded-lg p-4 transition-all cursor-pointer ${
            selectedPlan.id === PAYMENT_PLANS.ANNUAL.id
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
          }`}
          onClick={() => handleSelectPlan(PAYMENT_PLANS.ANNUAL)}
        >
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-lg">{PAYMENT_PLANS.ANNUAL.name}</h3>
              <p className="text-gray-600 text-sm mt-1">{PAYMENT_PLANS.ANNUAL.description}</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-gray-800">
                ${PAYMENT_PLANS.ANNUAL.price}
              </span>
              <span className="text-gray-500 text-sm">/year</span>
            </div>
          </div>
        </div>
      </div>
      
      {error && <div className="text-red-500 mb-4">{error}</div>}
      
      <div className="flex justify-between">
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
          onClick={handleNextStep}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : 'Continue to Checkout'}
        </button>
      </div>
    </div>
  );

  // Create a portal for the modal to render at the document body level
  return ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[999999]">
      {/* Backdrop */}
      <motion.div 
        className="absolute inset-0 bg-black bg-opacity-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <motion.div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md z-[999999] relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 500 }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>

        {/* Content */}
        <div className="p-6">
          {renderPlanSelection()}
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

export default PremiumUpgradeModal;